<template>
  <div
    class="trezo-card bg-white dark:bg-[#0c1427] mb-[25px] p-[25px] rounded-md"
  >
    <div class="trezo-card-content">
      <div class="flex items-center justify-between">
        <div class="flex items-center">
          <span class="block"> {{ directT('dashboard.totalCustomers') }} </span>
          <span
            class="inline-block px-[8.3px] py-[1px] border text-sm ltr:ml-[10px] rtl:mr-[10px] rounded-[100px]"
            :class="percentageChange >= 0 ? 
              'text-success-700 border-success-300 bg-success-100' : 
              'text-danger-700 border-danger-300 bg-danger-100'"
          >
            {{ percentageChange >= 0 ? '+' : '' }}{{ percentageChange }}%
          </span>
        </div>
        <span class="block text-sm"> {{ directT('dashboard.last7Days') }} </span>
      </div>
      <h5 class="text-[18px] md:text-[20px] mb-0 mt-[4px]">{{ loading ? '...' : totalCustomerCount }}</h5>
      <div class="mx-auto max-w-[300px] -mt-[33px] -mb-[20px]">
        <apexchart
          v-if="isClient && !loading"
          type="line"
          height="140"
          :options="totalCustomers"
          :series="customers"
        ></apexchart>
        <div v-else-if="loading" class="flex justify-center items-center h-[140px]">
          <div class="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
      <div class="flex items-center justify-between text-sm">
        <span class="block"> {{ startDate }} </span>
        <span class="block"> {{ endDate }} </span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed } from "vue";
import { useI18n } from "vue-i18n";
import { supabase } from '@/lib/supabaseClient';

// Direct import of translation files for manual translation lookup
import enTranslations from '@/i18n/locales/en.json';
import heTranslations from '@/i18n/locales/he.json';
import arTranslations from '@/i18n/locales/ar.json';

// Create type for translation object structure
type TranslationObject = Record<string, any>;

// Define types for customer data
interface CustomerData {
  id?: string;
  created_at?: string;
  [key: string]: any;  // Allow any additional properties
}

export default defineComponent({
  name: "TotalCustomersChart",
  setup() {
    const { locale } = useI18n();
    const isClient = ref(false);
    const loading = ref(true);
    const percentageChange = ref(0);
    const totalCustomerCount = ref(0);
    const startDate = ref("");
    const endDate = ref("");
    
    // Data for new customers and returning customers
    const newCustomersData = ref<number[]>([0, 0, 0, 0, 0, 0, 0]);
    const returningCustomersData = ref<number[]>([0, 0, 0, 0, 0, 0, 0]);

    // Store direct references to translation files
    const translationFiles = {
      en: enTranslations,
      he: heTranslations,
      ar: arTranslations
    };

    // Computed reactive property for customer data
    const customers = computed(() => [
      {
        name: "New Customers",
        data: newCustomersData.value,
      },
      {
        name: "Returning Customers",
        data: returningCustomersData.value,
      },
    ]);

    // Direct translation function that bypasses vue-i18n
    const directT = (key: string) => {
      const parts = key.split('.');
      let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];
      
      for (const part of parts) {
        if (!result || !(part in result)) {
          console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
          // Fallback to English if translation not found
          let enResult: TranslationObject = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
          }
          return enResult;
        }
        result = result[part as keyof typeof result] as TranslationObject;
      }
      return result;
    };

    const totalCustomers = ref({
      chart: {
        height: 140,
        type: "line",
        zoom: {
          enabled: false,
        },
        toolbar: {
          show: false,
        },
      },
      dataLabels: {
        enabled: false,
      },
      colors: ["#605DFF", "#C2CDFF"],
      stroke: {
        width: 2,
        curve: "straight",
      },
      grid: {
        borderColor: "#ffffff",
      },
      xaxis: {
        categories: [
          "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"
        ],
        axisTicks: {
          show: false,
          color: "#B1BBC8",
        },
        axisBorder: {
          show: false,
          color: "#B1BBC8",
        },
        labels: {
          show: false,
          style: {
            colors: "#8695AA",
            fontSize: "12px",
          },
        },
      },
      yaxis: {
        show: false,
        labels: {
          style: {
            colors: "#64748B",
            fontSize: "12px",
          },
        },
      },
      legend: {
        show: false,
        fontSize: "12px",
        position: "bottom",
        horizontalAlign: "center",
        itemMargin: {
          horizontal: 8,
          vertical: 0,
        },
        labels: {
          colors: "#64748B",
        },
        markers: {
          width: 9,
          height: 9,
          offsetX: -2,
          offsetY: -0.5,
        },
      },
    });

    // Format date for display
    const formatDate = (date: Date): string => {
      const options: Intl.DateTimeFormatOptions = { 
        day: '2-digit', 
        month: 'short'
      };
      
      if (locale.value === 'he') {
        return date.toLocaleDateString('he-IL', options);
      } else if (locale.value === 'ar') {
        return date.toLocaleDateString('ar-SA', options);
      }
      
      return date.toLocaleDateString('en-US', options);
    };

    // Create fallback sample data if needed
    const createSampleData = () => {
      // Sample data for new and returning customers
      newCustomersData.value = [35, 28, 45, 36, 54, 48, 63];
      returningCustomersData.value = [25, 34, 40, 55, 43, 61, 58];
      
      totalCustomerCount.value = 1528;
      percentageChange.value = 5.4;
      
      // Set date range for last 7 days (dummy)
      const now = new Date();
      const sevenDaysAgo = new Date(now);
      sevenDaysAgo.setDate(now.getDate() - 6);
      
      startDate.value = formatDate(sevenDaysAgo);
      endDate.value = formatDate(now);
    };

    // Function to fetch customer data
    const fetchCustomerData = async () => {
      loading.value = true;
      console.log("Starting fetchCustomerData in TotalCustomersChart...");
      
      try {
        // Check Supabase connection
        if (!supabase) {
          console.error("Supabase client is not available");
          createSampleData();
          return;
        }
        
        // Calculate date range
        const now = new Date();
        const sevenDaysAgo = new Date(now);
        sevenDaysAgo.setDate(now.getDate() - 6); // Last 7 days, including today
        
        const fourteenDaysAgo = new Date(now);
        fourteenDaysAgo.setDate(now.getDate() - 13); // Previous 7 days for comparison
        
        // Format dates for display
        startDate.value = formatDate(sevenDaysAgo);
        endDate.value = formatDate(now);
        
        // Try to get customers from clients table
        console.log("Querying clients table...");
        let { data: customers, error } = await supabase
          .from('clients')
          .select('*');
        
        // If clients table doesn't exist or is empty, try user_profiles table
        if (error || !customers || customers.length === 0) {
          console.log("No data in clients table, trying user_profiles...");
          const { data: users, error: usersError } = await supabase
            .from('user_profiles')
            .select('*');
            
          if (usersError || !users || users.length === 0) {
            console.warn("No customer data found in database");
            createSampleData();
            return;
          }
          
          customers = users;
        }
        
        console.log(`Found ${customers.length} customers in database`);
        
        // Process the customer data
        const typedCustomers: CustomerData[] = customers.map(customer => customer as unknown as CustomerData);
        
        // Set total customer count
        totalCustomerCount.value = typedCustomers.length;
        
        // Filter customers created in the last 7 days
        const recentCustomers = typedCustomers.filter(customer => {
          if (!customer.created_at) return false;
          const createdDate = new Date(customer.created_at);
          return createdDate >= sevenDaysAgo && createdDate <= now;
        });
        
        // Filter customers created in the previous 7 days
        const previousPeriodCustomers = typedCustomers.filter(customer => {
          if (!customer.created_at) return false;
          const createdDate = new Date(customer.created_at);
          return createdDate >= fourteenDaysAgo && createdDate < sevenDaysAgo;
        });
        
        // Calculate percentage change
        if (previousPeriodCustomers.length > 0) {
          const change = ((recentCustomers.length - previousPeriodCustomers.length) / previousPeriodCustomers.length) * 100;
          percentageChange.value = Math.round(change * 10) / 10; // Round to 1 decimal place
        } else if (recentCustomers.length > 0) {
          percentageChange.value = 100;
        } else {
          percentageChange.value = 0;
        }
        
        console.log("Recent customers:", recentCustomers.length);
        console.log("Previous period customers:", previousPeriodCustomers.length);
        console.log("Percentage change:", percentageChange.value);
        
        // Initialize arrays for daily counts with 0
        const newCustomersByDay = [0, 0, 0, 0, 0, 0, 0]; // Mon-Sun
        const returningCount = [0, 0, 0, 0, 0, 0, 0]; // Mon-Sun
        
        // Calculate daily new customers
        recentCustomers.forEach(customer => {
          if (customer.created_at) {
            try {
              const createdDate = new Date(customer.created_at);
              if (!isNaN(createdDate.getTime())) {
                const dayOfWeek = createdDate.getDay(); // 0 = Sunday, 6 = Saturday
                // Convert to Monday-Sunday format (0 = Monday, 6 = Sunday)
                const adjustedDay = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
                newCustomersByDay[adjustedDay] += 1;
              }
            } catch (error) {
              console.error("Error processing customer date:", error);
            }
          }
        });
        
        // For returning customers, create a plausible distribution based on total customers
        // This is just an approximation since we don't have actual "returning" data
        const totalExistingCustomers = totalCustomerCount.value - recentCustomers.length;
        
        // Create a reasonable distribution for returning customers
        // (in a real app, you'd query actual login or visit data)
        if (totalExistingCustomers > 0) {
          const baseDistribution = [12, 15, 14, 16, 18, 13, 10];
          const sum = baseDistribution.reduce((a, b) => a + b, 0);
          
          returningCount.forEach((_, idx) => {
            returningCount[idx] = Math.round((baseDistribution[idx] / sum) * totalExistingCustomers * 0.3 / 7);
          });
        }
        
        // Set data for charts
        newCustomersData.value = newCustomersByDay;
        returningCustomersData.value = returningCount;
        
        console.log("New customers by day:", newCustomersData.value);
        console.log("Returning customers by day:", returningCustomersData.value);
        
        // If both arrays are empty, use sample data
        if (newCustomersData.value.every(v => v === 0) && returningCustomersData.value.every(v => v === 0)) {
          console.warn("No daily customer data, using sample distribution");
          newCustomersData.value = [35, 28, 45, 36, 54, 48, 63];
          returningCustomersData.value = [25, 34, 40, 55, 43, 61, 58];
        }
        
      } catch (err) {
        console.error('Error processing customer data:', err);
        createSampleData();
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => {
      isClient.value = true;
      console.log("TotalCustomersChart mounted, initiating data fetch...");
      fetchCustomerData();
    });

    return {
      isClient,
      loading,
      customers,
      totalCustomers,
      directT,
      totalCustomerCount,
      percentageChange,
      startDate,
      endDate
    };
  },
});
</script>
